import { Rows } from 'base/ui/layout';
import { useLiveRef } from 'base/useLiveRef';
import classNames from 'classnames';
import React from 'react';
import IconRelumeColorLarge from '../relume_color_large.react.svg';
import styles from './loading_curtain.css';

// FPS we consider good enough.
const GOAL_FPS = 15;

// How many frames we'd like at the goal FPS before we consider the loading done.
const GOOD_FRAME_COUNT = 10;

// How many frames we'll wait for before giving up and removing the curtain anyway.
const MAX_FRAME_COUNT = 100;

export const WireframeLoadingCurtain = ({
  children,
  onFinishedRendering,
}: React.PropsWithChildren<{ onFinishedRendering?: () => void }>) => {
  const [initialFrameRendered, setInitialFrameRendered] = React.useState(false);
  const [rendering, setRendering] = React.useState(true);

  const previousTimeRef = React.useRef<number | null>(null);
  const goodFrameCountRef = React.useRef(0);
  const counterRef = React.useRef(0);

  const onFinishedRenderingRef = useLiveRef(onFinishedRendering);

  const callback = React.useCallback(() => {
    requestAnimationFrame((t) => {
      if (previousTimeRef.current !== null) {
        const latencyMs = t - previousTimeRef.current;
        const fps = 1000 / latencyMs;
        if (fps >= GOAL_FPS) {
          goodFrameCountRef.current += 1;
        }
      }
      previousTimeRef.current = t;
      counterRef.current += 1;

      if (
        goodFrameCountRef.current > GOOD_FRAME_COUNT ||
        counterRef.current > MAX_FRAME_COUNT
      ) {
        setRendering(false);
        onFinishedRenderingRef.current?.();
        return;
      }

      callback();
    });
  }, [onFinishedRenderingRef]);

  // We want the loading indicator to be rendered before we start rendering the children.
  const onAnimationStart = React.useCallback(() => {
    setInitialFrameRendered(true);

    // Kick off the frame counting.
    callback();
  }, [callback]);

  const [fadedOut, setFadedOut] = React.useState(false);

  const onTransitionEnd = React.useCallback(() => {
    setFadedOut(true);
  }, []);

  return (
    <>
      {initialFrameRendered && children}
      {!fadedOut && (
        <div
          className={classNames(
            styles.loadingCurtain,
            !rendering && styles.fadeOut,
          )}
          onAnimationStart={onAnimationStart}
          onTransitionEnd={onTransitionEnd}
        >
          <ProgressBar hideIcon />
        </div>
      )}
    </>
  );
};

export const AppLoadingCurtain = ({
  loading,
  children,
}: React.PropsWithChildren<{ loading: boolean }>) => {
  const [fadedOut, setFadedOut] = React.useState(false);

  const onTransitionEnd = React.useCallback(() => {
    setFadedOut(true);
  }, []);

  return (
    <>
      {children}
      {!fadedOut && (
        <div
          className={classNames(
            styles.loadingCurtain,
            !loading && styles.fadeOut,
          )}
          onTransitionEnd={onTransitionEnd}
        >
          <ProgressBar />
        </div>
      )}
    </>
  );
};

export const ProgressBar = ({ hideIcon }: { hideIcon?: boolean }) => (
  <Rows spacing={16} xalign="center">
    <div className={hideIcon ? styles.hideIcon : undefined}>
      <IconRelumeColorLarge />
    </div>
    <div className={styles.progressBar}>
      <div className={styles.fill} />
    </div>
  </Rows>
);
